// Badges, empty/error states, section headers, the spinner —
// what a page shows when it has nothing else to show.

import type { ReactNode } from "react";
import { cx } from "@/platform/ui/primitives/cx.js";

// ---- Badge ----
/** A status pill in `color`, any CSS colour or token, on a tint of it. */
export function Badge({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <span
      className="inline-flex min-w-[20px] items-center justify-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{
        color: color ?? "var(--color-muted)",
        background: color
          ? `color-mix(in oklab, ${color} 13%, transparent)`
          : "var(--color-surface-2)",
      }}
    >
      {children}
    </span>
  );
}

// ---- UnreadBadge ----
// A solid, high-contrast corner-style pill for unread counts — the look
// already used for the nav's Feeds "99+" indicator. `Badge` (above) is a
// flatter, translucent pill meant for general-purpose tags; this is
// specifically for "N unread" counts, which need to read as a real badge.
// `accent` reserves the bright treatment for "new since last viewed" (Feeds'
// freshness highlight) — plain unread is the muted variant.
export function UnreadBadge({
  count,
  accent = false,
  variant = "solid",
  className,
}: {
  count: number;
  accent?: boolean;
  /** "outline" is a negative-space pill (transparent bg, colored border and
   *  matching text) — used where a solid badge would be too loud, e.g. a
   *  collapsed category's count. */
  variant?: "solid" | "outline";
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex h-4 min-w-[16px] flex-none items-center justify-center rounded-full px-1 text-[9px] font-extrabold",
        variant === "outline"
          ? cx(
              "border bg-transparent",
              accent ? "border-accent text-accent" : "border-line-strong text-muted",
            )
          : accent
            ? "bg-accent text-bg"
            : "border border-line bg-surface-2 text-ink-soft",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

// ---- EmptyState ----
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line-strong bg-surface/50 px-6 py-12 text-center">
      {icon && <div className="mb-3 text-faint">{icon}</div>}
      <div className="text-sm font-semibold text-ink-soft">{title}</div>
      {description && <div className="mt-1.5 max-w-md text-sm text-muted">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  action,
}: {
  title?: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-negative/30 bg-negative/10 px-4 py-3">
      <div className="text-sm font-semibold text-negative">{title}</div>
      {description && <div className="mt-1 text-sm text-ink-soft">{description}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// ---- Spinner ----
export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted">
      <span className="pulse h-2 w-2 rounded-full bg-accent" />
      {label ?? "Loading…"}
    </div>
  );
}
